import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PropertyMap from '../components/PropertyMap';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getProperties } from '../api/api';
import { BedDouble, Users, MapPin, CheckCircle, Award, Clock, Shield, Home, Users2, Briefcase } from 'lucide-react';

const HomePage = () => {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProperties = async () => {
      try {
        const properties = await getProperties(true);
        setFeaturedProperties(properties.slice(0, 6));
      } catch (error) {
        console.error('Error loading properties:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProperties();
  }, []);

  const benefits = [
    {
      icon: <Clock size={32} />,
      title: 'Flexible Stays',
      description: 'Short and medium-term accommodation designed around project timelines, relocations and contract work. Stability for your team, without long-term commitment or hotel constraints.'
    },
    {
      icon: <CheckCircle size={32} />,
      title: 'Easy Enquiry',
      description: 'No booking platforms or third-party systems. Just a straightforward enquiry process, quick responses and direct communication throughout the stay.'
    },
    {
      icon: <Award size={32} />,
      title: 'All Managed',
      description: 'All properties are professionally managed, including cleaning, linen, utilities and maintenance, ensuring consistent standards for every stay.'
    },
    {
      icon: <Briefcase size={32} />,
      title: 'Work Ready',
      description: 'Comfortable, practical spaces designed for contractors, project teams and business travellers who need privacy, reliability and a place that works.'
    },
    {
      icon: <Home size={32} />,
      title: 'Local Support',
      description: 'Based in Chester and serving Ellesmere Port and Merseyside. Dependable accommodation close to work sites, transport links and key amenities.'
    },
    {
      icon: <Shield size={32} />,
      title: 'Integrity',
      description: 'We take the time to understand your requirements upfront, ensuring the right property is matched to the right stay, with clarity from day one.'
    }
  ];

  const testimonials = [
    {
      quote: 'Great service throughout. We used several StayX properties across the Wirral while working on The Open at Hoylake and would happily use them again when travelling to Royal Birkdale in 2026.',
      name: 'Project Lead',
      role: 'Major Sporting Event Contractor'
    },
    {
      quote: 'Chester Court was a great property for our large team of contractors working at Shotton Paper Mill. Coming from the Wirral, it was very accessible with a straightforward 25-minute drive up the A41.',
      name: 'Contracts Manager',
      role: 'Construction Contractor – Wirral'
    },
    {
      quote: 'We had a team of managers staying at the Bedford property and found it ideal. The house was finished to a very high standard, and the separate outbuilding with its own kitchen and bathroom was perfect.',
      name: 'Manager',
      role: 'Engineering Firm – Cheshire'
    }
  ];

  return (
    <div className="min-h-screen" style={{ background: '#f8f9fa' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="hero-section text-white py-24 md:py-32 relative">
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="inline-block mb-6">
            <span className="badge-premium">Premium Serviced Accommodation</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" data-testid="hero-title" style={{ letterSpacing: '-0.02em' }}>
            Luxury Stays.
            <br />
            <span style={{ color: '#d4a574' }}>Effortless Experience.</span>
          </h1>
          <p className="text-xl md:text-2xl mb-4 max-w-3xl mx-auto" style={{ color: '#e8e8e8', fontWeight: '300' }}>
            Discover premium serviced accommodation across Wirral, Chester, Ellesmere Port and Merseyside.
          </p>
          <p className="text-lg mb-10 max-w-2xl mx-auto" style={{ color: '#b8b8b8' }}>
            Designed for comfort, style, and total ease.
          </p>
          <Link to="/enquiry">
            <Button data-testid="hero-enquire-button" size="lg" className="btn-primary-custom text-lg px-10 py-7 rounded-full font-semibold">
              Make an Enquiry
            </Button>
          </Link>
        </div>
      </section>

      {/* Business Accommodation Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm font-bold mb-3" style={{ color: '#d4a574', letterSpacing: '2px' }}>BUSINESS ACCOMMODATION IN THE NORTH WEST</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 section-title" style={{ color: '#1a1a2e' }}>Accommodation, Handled Properly.</h2>
            <p className="text-xl mb-6" style={{ color: '#636e72', lineHeight: '1.8' }}>
              We provide fully serviced accommodation for companies, contractors and project teams across Chester, Ellesmere Port and Merseyside.
            </p>
            <p className="text-lg italic" style={{ color: '#2d3436', fontWeight: '500' }}>
              Our properties are designed for working professionals who need reliable, comfortable accommodation for short and medium-term stays.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20" style={{ background: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 section-title" style={{ color: '#1a1a2e' }}>Why Companies Choose Us</h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: '#636e72' }}>
              We work with organisations that need reliable, flexible accommodation for staff working away from home.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="benefit-card rounded-2xl">
                <CardContent className="p-8">
                  <div className="mb-4" style={{ color: '#d4a574' }}>
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: '#1a1a2e' }}>{benefit.title}</h3>
                  <p style={{ color: '#636e72', lineHeight: '1.7' }}>{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Properties Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-sm font-bold mb-3" style={{ color: '#d4a574', letterSpacing: '2px' }}>SERVICED ACCOMMODATION LOCATIONS</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 section-title" style={{ color: '#1a1a2e' }}>Featured Properties</h2>
            <p className="text-xl" style={{ color: '#636e72' }}>
              Properties Across the North West
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p style={{ color: '#636e72' }}>Loading properties...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.map((property) => (
                <Link key={property.id} to={`/properties/${property.id}`}>
                  <Card className="property-card h-full rounded-2xl" data-testid={`property-card-${property.id}`}>
                    <div className="h-56 overflow-hidden relative">
                      <img 
                        src={property.images[0]} 
                        alt={property.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 right-4">
                        <span className="badge-premium">Featured</span>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center text-sm mb-2" style={{ color: '#d4a574' }}>
                        <MapPin size={16} className="mr-1" />
                        {property.location}
                      </div>
                      <h3 className="text-2xl font-bold mb-3" style={{ color: '#1a1a2e' }}>{property.name}</h3>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-4 text-sm" style={{ color: '#636e72' }}>
                          <div className="flex items-center">
                            <BedDouble size={16} className="mr-1" />
                            {property.bedrooms} bedrooms
                          </div>
                          <div className="flex items-center">
                            <Users size={16} className="mr-1" />
                            {property.guests} guests
                          </div>
                        </div>
                        <p className="text-xl font-bold" style={{ color: '#d4a574' }}>
                          £{property.nightly_rate}/night
                        </p>
                      </div>
                      <Button data-testid={`view-property-${property.id}`} className="w-full btn-primary-custom rounded-full">
                        View Property
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/properties">
              <Button data-testid="view-all-properties-button" size="lg" className="btn-primary-custom px-10 py-6 text-lg rounded-full">
                View All Properties
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-sm font-bold mb-3" style={{ color: '#d4a574', letterSpacing: '2px' }}>PROPERTY LOCATIONS</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 section-title" style={{ color: '#1a1a2e' }}>Find Properties Near You</h2>
            <p className="text-xl" style={{ color: '#636e72' }}>
              Explore our serviced accommodation across Chester, Ellesmere Port and Merseyside
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <PropertyMap properties={featuredProperties} />
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20" style={{ background: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 section-title" style={{ color: '#1a1a2e' }}>Trusted by Contractors & Project Teams</h2>
            <p className="text-xl" style={{ color: '#636e72' }}>Here's what they have to say about staying with StayX</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial-card">
                <CardContent className="p-0">
                  <p className="mb-6 italic text-lg" style={{ color: '#2d3436', lineHeight: '1.8' }}>"{testimonial.quote}"</p>
                  <div className="border-t pt-4" style={{ borderColor: '#e8e8e8' }}>
                    <p className="font-bold" style={{ color: '#1a1a2e' }}>{testimonial.name}</p>
                    <p className="text-sm" style={{ color: '#636e72' }}>{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f4c75 100%)' }}>
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Book Your Stay?</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto" style={{ color: '#e8e8e8' }}>Get in touch today and we'll find the perfect accommodation for your needs.</p>
          <Link to="/enquiry">
            <Button data-testid="cta-enquire-button" size="lg" className="btn-primary-custom px-10 py-6 text-lg rounded-full">
              Make an Enquiry
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;